<script setup lang="ts">
import {
  computed,
  onMounted,
  onUnmounted,
  useId,
  useTemplateRef,
  watch,
} from "vue";
import { isJust, toMaybe, type Maybe } from "../../lib/maybe";
import {
  useAnchoredFloating,
  type AnchorSource,
} from "../../composables/useAnchoredFloating";

const props = withDefaults(
  defineProps<{
    /** Short name on the trigger (e.g. Size, Coast). */
    label: string;
    /** Current value shown on the trigger (e.g. 96, 40%). */
    valueText: string;
    min: number;
    max: number;
    step?: number;
    disabled?: boolean;
    /**
     * When the inner value changes (tool switch, etc.), close the popover.
     * Pass `nothing` when there is no external dismiss key.
     */
    dismissKey: Maybe<string | number | boolean>;
  }>(),
  {
    step: 1,
    disabled: false,
  },
);

const model = defineModel<number>({ required: true });
const open = defineModel<boolean>("open", { default: false });

const emit = defineEmits<{
  /** Fired once when the user starts dragging the range (for undo snapshots). */
  adjustStart: [];
}>();

const root = useTemplateRef<HTMLElement>("root");
const panel = useTemplateRef<HTMLElement>("panel");
const panelId = useId();
const inputId = useId();

const anchor = computed<AnchorSource>(() => toMaybe(root.value));
const panelMaybe = computed(() => toMaybe(panel.value));
const { floatingStyle, side } = useAnchoredFloating(anchor, panelMaybe, open, {
  placement: "bottom-start",
  gap: 6,
});

function setOpen(value: boolean): void {
  if (props.disabled && value) return;
  open.value = value;
}

function toggle(): void {
  setOpen(open.value === false);
}

function close(): void {
  open.value = false;
}

function onDocPointer(event: PointerEvent): void {
  if (open.value === false) return;
  const target = event.target;
  if (target instanceof Node === false) {
    close();
    return;
  }
  const rootEl = root.value;
  const panelEl = panel.value;
  if (
    (rootEl !== null && rootEl.contains(target) === true) ||
    (panelEl !== null && panelEl.contains(target) === true)
  ) {
    return;
  }
  close();
}

function onKeydown(event: KeyboardEvent): void {
  if (open.value === false || event.key !== "Escape") return;
  event.preventDefault();
  event.stopPropagation();
  close();
}

onMounted(() => {
  window.addEventListener("pointerdown", onDocPointer, true);
  window.addEventListener("keydown", onKeydown, true);
});

onUnmounted(() => {
  window.removeEventListener("pointerdown", onDocPointer, true);
  window.removeEventListener("keydown", onKeydown, true);
});

const nothingDismissSentinel = Symbol("zw-range-popover-no-dismiss");

watch(
  () =>
    isJust(props.dismissKey) ? props.dismissKey.value : nothingDismissSentinel,
  () => {
    close();
  },
);

/** Prefer the caller label; never leave the chip blank if the model has a number. */
const shownValue = computed(() => {
  const text = props.valueText.trim();
  if (text.length > 0) return text;
  return Number.isFinite(model.value) ? String(model.value) : "";
});
function onRangeKeyDown(event: KeyboardEvent): void {
  if (
    event.repeat === false &&
    [
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
      "PageUp",
      "PageDown",
    ].includes(event.key)
  )
    emit("adjustStart");
}
</script>

<template>
  <div ref="root" class="zw-range-popover">
    <button
      type="button"
      class="zw-range-popover-trigger"
      :class="{ 'zw-range-popover-trigger-open': open }"
      :disabled="disabled"
      :aria-expanded="open"
      aria-haspopup="dialog"
      :aria-controls="panelId"
      :aria-label="`${label}: ${shownValue}`"
      @click="toggle"
    >
      <span class="zw-range-popover-name">{{ label }}</span>
      <span class="zw-range-popover-value">{{ shownValue }}</span>
      <svg
        class="zw-range-popover-caret"
        width="10"
        height="10"
        viewBox="0 0 10 10"
        aria-hidden="true"
      >
        <path
          d="M2.2 3.6 5 6.4 7.8 3.6"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>
    <Teleport to="body">
      <div
        v-if="open"
        :id="panelId"
        ref="panel"
        class="zw-range-popover-panel"
        :data-side="side"
        role="dialog"
        :aria-label="label"
        :style="floatingStyle"
      >
        <label class="zw-range-popover-field" :for="inputId">
          <span class="zw-range-popover-field-label">{{ label }}</span>
          <span class="zw-range-popover-field-value">{{ shownValue }}</span>
        </label>
        <input
          :id="inputId"
          v-model.number="model"
          type="range"
          class="zw-range-popover-input"
          :min="min"
          :max="max"
          :step="step"
          :aria-label="label"
          @pointerdown="emit('adjustStart')"
          @keydown="onRangeKeyDown"
        />
      </div>
    </Teleport>
  </div>
</template>

<style>
.zw-range-popover {
  position: relative;
  display: inline-flex;
}

.zw-range-popover-trigger {
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 1.85rem;
  padding: 0 0.45rem 0 0.55rem;
  border: 1px solid var(--line);
  border-radius: 0.25rem;
  background: var(--bg);
  color: var(--text);
  font: inherit;
  font-size: 0.8125rem;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
}

.zw-range-popover-trigger:hover:not(:disabled) {
  background: var(--hover-fill);
}

.zw-range-popover-trigger:disabled {
  opacity: 0.4;
  cursor: default;
}

.zw-range-popover-trigger-open {
  border-color: color-mix(in oklch, var(--comment) 55%, var(--line));
  background: var(--hover-fill);
  color: var(--text-strong);
}

.zw-range-popover-name {
  color: var(--text-muted);
  font-size: 0.75rem;
  letter-spacing: 0.01em;
}

.zw-range-popover-value {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: var(--text-strong);
  min-width: 2.25rem;
  text-align: right;
}

.zw-range-popover-caret {
  flex-shrink: 0;
  opacity: 0.7;
  transition: transform var(--duration-fast, 150ms)
    var(--ease-out-quart, cubic-bezier(0.25, 1, 0.5, 1));
}

.zw-range-popover-trigger-open .zw-range-popover-caret {
  transform: rotate(180deg);
}

.zw-range-popover-panel {
  z-index: 500;
  box-sizing: border-box;
  width: 12.5rem;
  padding: 0.55rem 0.65rem 0.7rem;
  border: 1px solid var(--line);
  border-radius: 0.35rem;
  background: var(--bg-raised);
  font-family: var(--font-ui);
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.zw-range-popover-field {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: var(--text-muted);
}

.zw-range-popover-field-value {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: var(--text-strong);
  font-size: 0.8125rem;
}

.zw-range-popover-input {
  width: 100%;
  margin: 0;
  accent-color: var(--comment);
}

@media (prefers-reduced-motion: reduce) {
  .zw-range-popover-caret {
    transition: none;
  }
}
</style>
